import React, { useEffect, useRef, useState } from "react";
import { IconButton } from "@mui/material";
import SoftBox from "../SoftBox";
import SoftButton from "../SoftButton";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import { getOrientedImageObjectUrl } from "../../helpers/imageOrientation";
import { UPDATE_EKPI_IMAGE_ROTATION } from "../../api/kpi";

/**
 * KPI timeline image (yard sign, etc.) — portrait/landscape + EXIF correction.
 * Rotate uses CSS only so we never re-encode blobs (avoids transparency glitches).
 * Optional save persists rotation to EmployeeKpi.metadata.imageRotation.
 */
function KpiReferenceImage({
  src,
  alt = "Reference Item",
  maxHeight = "75vh",
  entryId = null,
  savedRotation = 0,
  onRotationSaved,
}) {
  const [displaySrc, setDisplaySrc] = useState(null);
  const [rotation, setRotation] = useState(savedRotation);
  const [persistedRotation, setPersistedRotation] = useState(savedRotation);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const objectUrlRef = useRef(null);

  const revokeObjectUrl = () => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  };

  useEffect(() => {
    setRotation(savedRotation);
    setPersistedRotation(savedRotation);
  }, [savedRotation, entryId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    revokeObjectUrl();

    (async () => {
      try {
        const url = await getOrientedImageObjectUrl(src);
        if (cancelled) {
          if (url.startsWith("blob:")) URL.revokeObjectURL(url);
          return;
        }
        objectUrlRef.current = url;
        setDisplaySrc(url);
      } catch {
        if (!cancelled) setDisplaySrc(src);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      revokeObjectUrl();
    };
  }, [src]);

  const handleSaveRotation = async () => {
    if (!entryId || rotation === persistedRotation || saving) return;

    setSaving(true);
    try {
      await UPDATE_EKPI_IMAGE_ROTATION(entryId, rotation);
      setPersistedRotation(rotation);
      onRotationSaved?.(rotation);
    } catch {
      // keep unsaved rotation so user can retry
    } finally {
      setSaving(false);
    }
  };

  const showSave = Boolean(entryId) && rotation !== persistedRotation;

  return (
    <SoftBox
      borderRadius="lg"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        maxWidth: "100%",
        gap: 1,
      }}
    >
      <SoftBox
        sx={{
          position: "relative",
          maxWidth: "100%",
          backgroundColor: "#fff",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        {!loading && displaySrc && (
          <img
            src={displaySrc}
            alt={alt}
            style={{
              display: "block",
              maxWidth: "100%",
              maxHeight,
              width: "auto",
              height: "auto",
              objectFit: "contain",
              transform: `rotate(${rotation}deg)`,
              transformOrigin: "center center",
            }}
          />
        )}
        {loading && (
          <SoftBox sx={{ minHeight: 120, minWidth: 120, backgroundColor: "#fff" }} />
        )}
        {!loading && (
          <SoftBox
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 1,
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
              alignItems: "flex-end",
            }}
          >
            <IconButton
              aria-label="Rotate image"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setRotation((deg) => (deg + 90) % 360);
              }}
              sx={{
                backgroundColor: "#ffffff !important",
                border: "1px solid #ced4da",
                boxShadow: "0 2px 6px rgba(0, 0, 0, 0.25)",
                color: "#344767",
                "&:hover": {
                  backgroundColor: "#f1f3f5 !important",
                },
                "&:focus": {
                  backgroundColor: "#ffffff !important",
                },
              }}
            >
              <RotateRightIcon fontSize="small" />
            </IconButton>
            {showSave && (
              <SoftButton
                size="small"
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSaveRotation();
                }}
                disabled={saving}
                sx={{
                  minWidth: "auto",
                  px: 1.5,
                  py: 0.5,
                  fontSize: "0.75rem",
                  backgroundColor: "primary.main !important",
                  color: "#fff !important",
                  boxShadow: "0 2px 6px rgba(0, 0, 0, 0.25)",
                  "&:hover": {
                    backgroundColor: "primary.dark !important",
                  },
                  "&:disabled": {
                    backgroundColor: "primary.main !important",
                    opacity: 0.7,
                  },
                }}
              >
                {saving ? "Saving…" : "Save"}
              </SoftButton>
            )}
          </SoftBox>
        )}
      </SoftBox>
    </SoftBox>
  );
}

export default KpiReferenceImage;
